import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { selectWarehouse, useCurrentWarehouseId } from '../../lib/warehouse';
import type { WarehouseListItem } from './types';

/**
 * 顶栏仓库切换器（Spec §5："选中的仓库上下文必须显而易见"）。替换 M1 时期的
 * 禁用占位按钮。切换后使所有仓库范围内的查询失效，让当前页面立刻用新的
 * X-Warehouse-Id 重新取数，而不是继续显示上一个仓库的数据。
 */
export function WarehouseSwitcher(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentId = useCurrentWarehouseId();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['warehouses', 'mine'],
    queryFn: () => apiFetch<{ items: WarehouseListItem[] }>('/warehouses/mine'),
  });

  const items = data?.items ?? [];
  const current = items.find((w) => w.id === currentId);

  const pick = async (id: string): Promise<void> => {
    selectWarehouse(id, false);
    setOpen(false);
    await queryClient.invalidateQueries({ queryKey: ['session', 'me'] });
    await queryClient.invalidateQueries({ queryKey: ['locations'] });
  };

  return (
    <div className="shell__warehouse-wrap">
      <button
        className="shell__warehouse"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="shell__warehouse-dot" aria-hidden="true" />
        {current?.name ?? t('shell.noWarehouse')}
      </button>

      {open && (
        <>
          <div className="shell__warehouse-scrim" onClick={() => setOpen(false)} />
          <div className="shell__warehouse-menu" role="menu">
            {items.map((w) => (
              <button
                key={w.id}
                type="button"
                role="menuitemradio"
                aria-checked={w.id === currentId}
                className="shell__warehouse-menu-item"
                onClick={() => void pick(w.id)}
              >
                {w.name}
                <span className="shell__warehouse-menu-code">{w.code}</span>
              </button>
            ))}
            <Link
              to="/warehouses"
              state={{ from: { pathname: window.location.pathname } }}
              className="shell__warehouse-menu-item shell__warehouse-menu-more"
              onClick={() => setOpen(false)}
            >
              {t('warehouses.viewAll')}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
